export const GOOGLE_OAUTH_MESSAGE_TYPE = "google-oauth-complete" as const;
export const GOOGLE_OAUTH_POPUP_STORAGE_KEY = "wizmate.google-oauth-complete";

export type GoogleOAuthPopupResult = "success" | "error" | null;

export type GoogleOAuthPopupMessage = {
  type: typeof GOOGLE_OAUTH_MESSAGE_TYPE;
  googleConnect: GoogleOAuthPopupResult;
  scopeBundle: string | null;
  errorCode: string | null;
};

export type GoogleOAuthPopupSnapshot = GoogleOAuthPopupMessage & {
  completedAt: number;
};


function normalizeGoogleConnect(value: string | null): GoogleOAuthPopupResult {
  return value === "success" || value === "error" ? value : null;
}

export function readGoogleOAuthPopupMessage(search: string): GoogleOAuthPopupMessage {
  const params = new URLSearchParams(search);
  const errorCode = params.get("errorCode");
  const scopeBundle = params.get("scopeBundle");
  const rawGoogleConnect = params.get("googleConnect");
  let googleConnect = normalizeGoogleConnect(rawGoogleConnect);

  const hasCompletionSignal =
    rawGoogleConnect !== null || errorCode !== null || scopeBundle !== null;
  if (googleConnect === null && hasCompletionSignal) {
    googleConnect = errorCode ? "error" : "success";
  }

  return {
    type: GOOGLE_OAUTH_MESSAGE_TYPE,
    googleConnect,
    scopeBundle,
    errorCode,
  };
}

export function isGoogleOAuthPopupMessage(value: unknown): value is GoogleOAuthPopupMessage {
  if (!value || typeof value !== "object") {
    return false;
  }

  const payload = value as Partial<GoogleOAuthPopupMessage>;
  return payload.type === GOOGLE_OAUTH_MESSAGE_TYPE;
}

export function createGoogleOAuthPopupSnapshot(message: GoogleOAuthPopupMessage): GoogleOAuthPopupSnapshot {
  return {
    ...message,
    completedAt: Date.now(),
  };
}

export function isGoogleOAuthPopupSnapshot(value: unknown): value is GoogleOAuthPopupSnapshot {
  if (!isGoogleOAuthPopupMessage(value)) {
    return false;
  }

  const snapshot = value as Partial<GoogleOAuthPopupSnapshot>;
  return typeof snapshot.completedAt === "number" && Number.isFinite(snapshot.completedAt);
}


export function writeGoogleOAuthPopupSnapshot(snapshot: GoogleOAuthPopupSnapshot) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(GOOGLE_OAUTH_POPUP_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // noop
  }
}


export function readGoogleOAuthPopupSnapshot() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(GOOGLE_OAUTH_POPUP_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as unknown;
    return isGoogleOAuthPopupSnapshot(parsed) ? parsed : null;
  } catch {
    return null;
  }
}


export function clearGoogleOAuthPopupSnapshot() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(GOOGLE_OAUTH_POPUP_STORAGE_KEY);
  } catch {
    // noop
  }
}

